import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchProjectsApi,
  fetchProjectByIdApi,
  createProjectApi,
  deleteProjectApi,
} from "../../services/api/boq/projectService";
import { fetchClientsApi } from "../../services/api/boq/clientService";
import toast from "react-hot-toast";
import { FaUserTimes } from "react-icons/fa";
import { FaEdit, FaTrash, FaEye } from "react-icons/fa";

export default function Projects() {
  const initialFormData = {
    project_code: "",
    client_id: "",
    project_code: "",
    project_name: "",
    project_type: "",
    billing_type: "",
    start_date: "",
    end_date: "",
    project_value: "",
    approved_budget: "",
    status: "active",
    project_manager_id: "",
    description: "",
    remarks: "",
    assigned_users: [],
  };

  const initialErrors = {};
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState(initialErrors);
  const [viewProject, setViewProject] = useState(null);
  const [deleteProjectData, setDeleteProjectData] = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [statusFilter, setStatusFilter] = useState("");
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState("");
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [managers, setManagers] = useState([]);
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);

  // ---------------- LOAD PROJECTS ----------------
  const loadProjects = async (page = 1) => {
    try {
      setLoading(true);
      const res = await fetchProjectsApi(page);
      const paginated = res.data.data;
      setProjects(paginated.data); // 👈 ONLY 10 items
      setCurrentPage(paginated.current_page);
      setLastPage(paginated.last_page);
      setTotal(paginated.total);
    } catch (err) {
      console.log(err);
      setError("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  // ---------------- LOAD CLIENTS & MANAGERS ----------------
  const loadClientsAndManagers = async () => {
    try {
      // Replace with your actual APIs
      // const clientsRes = await fetchClientsApi();
      // const usersRes = await fetchUsersApi();
      // setClients(clientsRes.data);
      // setManagers(usersRes.data);

      // TEMP placeholder
      setClients([
        { id: 1, client_name: "Client A" },
        { id: 2, client_name: "Client B" },
      ]);
      setManagers([
        { id: 1, name: "Manager A" },
        { id: 2, name: "Manager B" },
      ]);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    loadProjects(currentPage);
    loadClientsAndManagers();
  }, [currentPage]);

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    const res = await fetchClientsApi();
    const list = Array.isArray(res) ? res : res.data;

    const normalized = list.map((c) => ({
      ...c,
      client_name: c.client_name || c.name,
    }));

    setClients(normalized);
  };

  // ---------------- CANCEL CREATE ----------------
  const handleCancel = () => {
    setFormData(initialFormData);
    setErrors(initialErrors);
    setShowCreate(false);
  };

  const getClientName = (clientId) => {
    const client = clients.find((c) => c.id === clientId);
    return client ? client.client_name : "—";
  };

  // ---------------- GENERATE PROJECT CODE ----------------
  const generateProjectCode = () => {
    const timestamp = Date.now().toString().slice(-3);
    const random = Math.floor(100 + Math.random() * 900);
    return `PJR-${timestamp}${random}`;
  };

  // ---------------- CREATE PROJECT ----------------
  const createProject = async () => {
    if (!validateProject()) return;

    if (!formData.client_id) {
      setErrors((prev) => ({
        ...prev,
        client_id: "Client is required",
      }));
      return;
    }

    try {
      const payload = {
        client_id: formData.client_id,
        project_code: formData.project_code,
        project_name: formData.project_name,
        project_type: formData.project_type,

        start_date: formData.start_date,
        end_date: formData.end_date,

        project_value: Number(formData.project_value),
        approved_budget: Number(formData.approved_budget),
        actual_cost: 0,

        status: formData.status,
        billing_type: formData.billing_type,
        progress_percent: 0,

        description: formData.description || "",
        remarks: formData.remarks || "",

        project_manager_id: Number(formData.project_manager_id),
        assigned_users: formData.assigned_users || [],
      };
      await createProjectApi(payload);

      setShowCreate(false);
      setFormData(initialFormData);
      loadProjects();
      toast.success("Project created successfully!");
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to create project");
    }
  };

  // ---------------- DELETE PROJECT ----------------
  const deleteProject = async () => {
    try {
      await deleteProjectApi(deleteProjectData.id);
      setProjects(projects.filter((p) => p.id !== deleteProjectData.id));
      setShowDelete(false);
      setDeleteProjectData(null);
      toast.success("Project deleted successfully!");
    } catch (err) {
      console.log(err);
      toast.error("Delete failed");
    }
  };

  // ---------------- VIEW PROJECT ----------------
  const viewProjectDetails = async (project) => {
    try {
      const res = await fetchProjectByIdApi(project.id);

      setViewProject(res.data);
    } catch (err) {
      console.log(err);
      alert("Failed to load project details");
    }
  };

  // ---------------- STATUS COLOR ----------------
  const statusColor = (status) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-700";
      case "completed":
        return "bg-blue-100 text-blue-700";
      case "on hold":
        return "bg-yellow-100 text-yellow-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  // ---------------- FILTERED PROJECTS ----------------
  const filteredProjects = projects.filter((p) => {
    const clientName =
      clients.find((c) => c.id === p.client_id)?.client_name || "";
    const matchesSearch =
      p.project_name?.toLowerCase().includes(search.toLowerCase()) ||
      clientName?.toLowerCase().includes(search.toLowerCase());

    const matchesStatus = !statusFilter || p.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter]);

  // ---------------- SORT HANDLER ----------------
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const ErrorText = ({ error }) =>
    error ? <p className="text-red-500 text-xs mt-1">{error}</p> : null;

  // ---------------- VALIDATE PROJECT FORM ----------------
  const validateProject = () => {
    let temp = {};

    // if (!formData.project_code.trim())
    //   temp.project_code = "Project code is required";

    if (!formData.project_name.trim())
      temp.project_name = "Project name is required";

    if (!formData.client_id) temp.client_id = "Client is required";

    if (!formData.project_type) temp.project_type = "Project type is required";

    if (!formData.billing_type) temp.billing_type = "Billing type is required";

    if (!formData.project_manager_id)
      temp.project_manager_id = "Project manager is required";

    if (!formData.project_value || formData.project_value <= 0)
      temp.project_value = "Project value must be greater than 0";

    if (!formData.start_date) temp.start_date = "Start date is required";

    if (!formData.end_date) temp.end_date = "End date is required";

    if (
      formData.start_date &&
      formData.end_date &&
      new Date(formData.end_date) < new Date(formData.start_date)
    ) {
      temp.end_date = "End date must be after start date";
    }

    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  // ---------------- PROGRESS BAR ----------------
  const ProgressBar = ({ value = 0 }) => (
    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
      <div
        className="h-full bg-blue-600 transition-all"
        style={{ width: `${value}%` }}
      />
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-gray-600">Loading project...</p>
        </div>
      </div>
    );
  }

  if (error) return <p className="p-2 text-red-600">{error}</p>;

  return (
    <div className="p-2 bg-gray-100 min-h-screen">
      {/* HEADER */}
      <div className="bg-white rounded-xl shadow p-4">
        <div className="flex justify-between mb-4">
          <h2 className="text-xl font-semibold">Projects</h2>
          <button
            onClick={() => setShowCreate(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded"
          >
            + Create Project
          </button>
        </div>

        {/* SEARCH & FILTER */}
        <div className="flex justify-between items-center mb-4">
          <input
            type="text"
            placeholder="Search project / client..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border p-2 rounded w-1/3"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border p-2 rounded"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="on hold">On Hold</option>
          </select>
          <button
            onClick={() => {
              setSearch("");
              setStatusFilter("");
            }}
            className="px-3 py-2 bg-gray-200 rounded"
          >
            Reset
          </button>
        </div>

        {/* PROJECT TABLE */}
        <div className="bg-white rounded-xl shadow overflow-hidden">
          {/* PROJECT TABLE */}
          <div className="bg-white rounded-xl shadow overflow-hidden">
            {/* Desktop Table */}
            <table className="hidden md:table w-full text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left">#</th>
                  <th className="px-4 py-3 text-left">Project Code</th>
                  <th className="px-4 py-3 text-left">Project</th>
                  <th className="px-4 py-3 text-left">Client</th>
                  <th className="px-4 py-3 text-left">Team</th>
                  <th className="px-4 py-3 text-left">Attendance</th>
                  <th className="px-4 py-3 text-left">Type</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredProjects.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="py-6 text-center text-gray-500">
                      No projects found
                    </td>
                  </tr>
                ) : (
                  filteredProjects.map((p, index) => {
                    const client = clients.find((c) => c.id === p.client_id);
                    return (
                      <tr key={p.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          {(currentPage - 1) * 10 + index + 1}
                        </td>
                        <td className="px-4 py-3 font-mono">
                          {p.project_code}
                        </td>
                        <td className="px-4 py-3 font-medium">
                          {p.project_name}
                        </td>
                        <td className="px-4 py-3">
                          {client ? client.client_name : "—"}
                        </td>
                        {/* <td className="px-4 py-3">{p.manager_name || "—"}</td> */}
                        <td>
                          <button
                            onClick={() => navigate(`/projects/${p.id}/members`)}
                            className="flex items-center gap-2 px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-all border border-indigo-200 text-xs font-semibold shadow-sm"
                          >
                            Members
                          </button>
                        </td>
                        <td>
                          <button
                            onClick={() => navigate(`/projects/${p.id}/attendance`)}
                            className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-all border border-emerald-200 text-xs font-semibold shadow-sm"
                          >
                            Attendance
                          </button>
                        </td>
                        <td className="px-4 py-3">{p.project_type}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`px-3 py-1 rounded-full text-xs ${statusColor(p.status)}`}
                          >
                            {p.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center flex justify-center gap-2">
                          <button
                            onClick={() => navigate(`/projects/edit/${p.id}`)}
                            className="p-2 rounded-full bg-yellow-100 text-yellow-700"
                            title="Edit"
                          >
                            <FaEdit size={14} />
                          </button>
                          <button
                            onClick={() => navigate(`/projects/view/${p.id}`)}
                            className="p-2 rounded-full bg-blue-100 text-blue-700"
                            title="View"
                          >
                            <FaEye size={14} />
                          </button>
                          <button
                            onClick={() => {
                              setDeleteProjectData(p);
                              setShowDelete(true);
                            }}
                            className="p-2 rounded-full bg-red-100 text-red-700"
                            title="Delete"
                          >
                            <FaTrash size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>

            {/* Mobile List */}
            <div className="md:hidden divide-y">
              {filteredProjects.length === 0 ? (
                <p className="py-6 text-center text-gray-500">
                  No projects found
                </p>
              ) : (
                filteredProjects.map((p, index) => {
                  const client = clients.find((c) => c.id === p.client_id);
                  return (
                    <div
                      key={p.id}
                      className="p-4 border-b hover:bg-gray-50 rounded-lg mb-2"
                    >
                      <div className="flex justify-between items-center mb-2">
                        <h3 className="font-medium text-gray-800">
                          {p.project_name}
                        </h3>
                        <span
                          className={`px-2 py-1 rounded-full text-xs ${statusColor(p.status)}`}
                        >
                          {p.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">
                        Code: {p.project_code}
                      </p>
                      <p className="text-sm text-gray-500">
                        Client: {client ? client.client_name : "—"}
                      </p>
                      <p className="text-sm text-gray-500">
                        Manager: {p.manager_name || "—"}
                      </p>
                      <p className="text-sm text-gray-500">
                        Type: {p.project_type}
                      </p>

                      <div className="flex justify-end gap-2 mt-2">
                        <button
                          onClick={() => navigate(`/projects/edit/${p.id}`)}
                          className="p-2 rounded-full bg-yellow-100 text-yellow-700"
                          title="Edit"
                        >
                          <FaEdit size={14} />
                        </button>
                        <button
                          onClick={() => viewProjectDetails(p)}
                          className="p-2 rounded-full bg-blue-100 text-blue-700"
                          title="View"
                        >
                          <FaEye size={14} />
                        </button>
                        <button
                          onClick={() => {
                            setDeleteProjectData(p);
                            setShowDelete(true);
                          }}
                          className="p-2 rounded-full bg-red-100 text-red-700"
                          title="Delete"
                        >
                          <FaTrash size={14} />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <p className="text-sm text-gray-600 text-center mt-2">
            Showing {(currentPage - 1) * 10 + 1}–
            {Math.min(currentPage * 10, total)} of {total} projects
          </p>

          {/* Pagination Controls */}
          <div className="flex justify-center items-center mt-4 gap-2 text-sm">
            <button
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Prev
            </button>

            {Array.from({ length: lastPage }, (_, i) => (
              <button
                key={i + 1}
                onClick={() => setCurrentPage(i + 1)}
                className={`px-3 py-1 border rounded ${currentPage === i + 1 ? "bg-blue-600 text-white" : ""
                  }`}
              >
                {i + 1}
              </button>
            ))}

            <button
              onClick={() => setCurrentPage((p) => Math.min(p + 1, lastPage))}
              disabled={currentPage === lastPage}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>

        {/* VIEW PROJECT MODAL */}
        {viewProject && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-xl">
              <h3 className="text-2xl font-bold mb-1">
                {viewProject.project_name}
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                {viewProject.project_code}
              </p>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <p>
                  <strong>Client: </strong>
                  {clients.find((c) => c.id === viewProject.client_id)
                    ?.client_name || "N/A"}
                </p>
                <p>
                  <strong>Type:</strong> {viewProject.project_type}
                </p>
                <p>
                  <strong>Start Date:</strong>{" "}
                  {new Date(viewProject.start_date).toLocaleDateString()}
                </p>
                <p>
                  <strong>Deadline:</strong>{" "}
                  {new Date(viewProject.end_date).toLocaleDateString()}
                </p>
                <p>
                  <strong>Actual Budget:</strong> ₹
                  {viewProject.project_value || "N/A"}
                </p>
                <p>
                  <strong>Approved Budget:</strong> ₹
                  {viewProject.approved_budget || "N/A"}
                </p>
                <p>
                  <strong>Status:</strong>{" "}
                  <span
                    className={`px-2 py-1 rounded ${statusColor(viewProject.status)}`}
                  >
                    {viewProject.status}
                  </span>
                </p>
                <p>
                  <strong>Manager:</strong> {viewProject.manager_name || "N/A"}
                </p>
              </div>
              <div className="mt-4">
                <strong>Description:</strong>
                <p className="text-gray-600 text-sm mt-1">
                  {viewProject.description || "No description"}
                </p>
              </div>
              <div className="text-right mt-6">
                <button
                  onClick={() => setViewProject(null)}
                  className="px-4 py-2 bg-gray-300 rounded"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* CREATE PROJECT MODAL */}
        {showCreate && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
            <div className="bg-white p-6 rounded w-full max-w-2xl">
              <h3 className="text-lg font-semibold mb-4">Create Project</h3>

              <div className="grid grid-cols-2 gap-4">
                {/* Project Code (Auto-generated) */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Project Code
                  </label>
                  <input
                    type="text"
                    className="border p-2 w-full rounded bg-gray-100 cursor-not-allowed"
                    value={formData.project_code}
                    readOnly
                  />
                </div>

                {/* Project Name */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Project Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    placeholder="Project Name"
                    className={`border p-2 w-full rounded ${errors.project_name ? "border-red-500 focus:ring-red-500" : ""}`}
                    value={formData.project_name}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        project_name: e.target.value,
                      });
                      setErrors({ ...errors, project_name: "" });
                    }}
                  />
                  {errors.project_name && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.project_name}
                    </p>
                  )}
                </div>

                {/* Project Type */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Project Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    className={`border p-2 w-full rounded ${errors.project_type ? "border-red-500" : ""}`}
                    value={formData.project_type}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        project_type: e.target.value,
                      });
                      setErrors({ ...errors, project_type: "" });
                    }}
                  >
                    <option value="">Select Project Type</option>
                    <option value="Commercial">Commercial</option>
                    <option value="Residential">Residential</option>
                    <option value="Industrial">Industrial</option>
                    <option value="Infrastructure">Infrastructure</option>
                  </select>
                  {errors.project_type && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.project_type}
                    </p>
                  )}
                </div>

                {/* Billing Type */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Billing Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    className={`border p-2 w-full rounded ${errors.billing_type ? "border-red-500" : ""}`}
                    value={formData.billing_type}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        billing_type: e.target.value,
                      });
                      setErrors({ ...errors, billing_type: "" });
                    }}
                  >
                    <option value="">Select Billing Type</option>
                    <option value="BOQ">BOQ</option>
                    <option value="LUMPSUM">Lump Sum</option>
                  </select>
                  {errors.billing_type && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.billing_type}
                    </p>
                  )}
                </div>

                {/* Client Search */}
                <div className="relative col-span-2">
                  <label className="block text-sm font-medium mb-1">
                    Client <span className="text-red-500">*</span>
                  </label>
                  <input
                    placeholder="Search Client"
                    className={`border p-2 w-full rounded ${errors.client_id ? "border-red-500" : ""}`}
                    value={clientSearch}
                    onChange={(e) => {
                      setClientSearch(e.target.value);
                      setShowClientDropdown(true);
                    }}
                    onFocus={() => setShowClientDropdown(true)}
                  />

                  {errors.client_id && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.client_id}
                    </p>
                  )}

                  {showClientDropdown && (
                    <ul className="absolute z-20 bg-white border rounded w-full max-h-40 overflow-y-auto shadow">
                      {clients
                        .filter((c) =>
                          (c.client_name || "")
                            .toLowerCase()
                            .includes(clientSearch.toLowerCase()),
                        )
                        .map((client) => (
                          <li
                            key={client.id}
                            className="px-4 py-2 hover:bg-blue-50 cursor-pointer"
                            onClick={() => {
                              const code = generateProjectCode(); // ✅ now client is defined
                              setFormData({
                                ...formData,
                                client_id: client.id,
                                // client_name: client.client_name,
                                project_code: code,
                              });
                              setClientSearch(client.client_name);
                              setShowClientDropdown(false);
                              setErrors({ ...errors, client_id: "" });
                            }}
                          >
                            <p className="font-medium">{client.client_name}</p>
                            <p className="text-xs text-gray-500">
                              {client.client_code}
                            </p>
                          </li>
                        ))}
                    </ul>
                  )}
                </div>

                {/* Project Manager */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Project Manager <span className="text-red-500">*</span>
                  </label>
                  <select
                    className={`border p-2 w-full rounded ${errors.project_manager_id ? "border-red-500" : ""}`}
                    value={formData.project_manager_id}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        project_manager_id: e.target.value,
                      });
                      setErrors({ ...errors, project_manager_id: "" });
                    }}
                  >
                    <option value="">Select Manager</option>
                    {managers.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                  {errors.project_manager_id && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.project_manager_id}
                    </p>
                  )}
                </div>

                {/* Project Value */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Project Value <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    placeholder="Project Value"
                    className={`border p-2 w-full rounded ${errors.project_value ? "border-red-500" : ""}`}
                    value={formData.project_value}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        project_value: e.target.value,
                      });
                      setErrors({ ...errors, project_value: "" });
                    }}
                  />
                  {errors.project_value && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.project_value}
                    </p>
                  )}
                </div>

                {/* Approved Budget */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Approved Budget <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    placeholder="Approved Budget"
                    className={`border p-2 w-full rounded ${errors.approved_budget ? "border-red-500" : ""}`}
                    value={formData.approved_budget}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        approved_budget: e.target.value,
                      });
                      setErrors({ ...errors, approved_budget: "" });
                    }}
                  />
                  {errors.approved_budget && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.approved_budget}
                    </p>
                  )}
                </div>

                {/* Start Date */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Start Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    className={`border p-2 w-full rounded ${errors.start_date ? "border-red-500" : ""}`}
                    value={formData.start_date}
                    onChange={(e) => {
                      setFormData({ ...formData, start_date: e.target.value });
                      setErrors({ ...errors, start_date: "" });
                    }}
                  />
                  {errors.start_date && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.start_date}
                    </p>
                  )}
                </div>

                {/* End Date */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    End Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    className={`border p-2 w-full rounded ${errors.end_date ? "border-red-500" : ""}`}
                    value={formData.end_date}
                    onChange={(e) => {
                      setFormData({ ...formData, end_date: e.target.value });
                      setErrors({ ...errors, end_date: "" });
                    }}
                  />
                  {errors.end_date && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.end_date}
                    </p>
                  )}
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Status <span className="text-red-500">*</span>
                  </label>
                  <select
                    className={`border p-2 w-full rounded ${errors.status ? "border-red-500" : ""}`}
                    value={formData.status}
                    onChange={(e) => {
                      setFormData({ ...formData, status: e.target.value });
                      setErrors({ ...errors, status: "" });
                    }}
                  >
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                    <option value="on hold">On Hold</option>
                  </select>
                  {errors.status && (
                    <p className="text-red-500 text-xs mt-1">{errors.status}</p>
                  )}
                </div>

                {/* Description */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">
                    Description
                  </label>
                  <textarea
                    placeholder="Description"
                    className={`border p-2 w-full rounded ${errors.description ? "border-red-500" : ""}`}
                    value={formData.description}
                    onChange={(e) => {
                      setFormData({ ...formData, description: e.target.value });
                      setErrors({ ...errors, description: "" });
                    }}
                  />
                  {errors.description && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.description}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-5 flex justify-end gap-2">
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 bg-gray-300 rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={createProject}
                  className="px-4 py-2 bg-blue-600 text-white rounded"
                >
                  Create Project
                </button>
              </div>
            </div>
          </div>
        )}

        {/* DELETE CONFIRMATION */}
        {showDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-sm">
              <p>
                Are you sure you want to delete project{" "}
                <strong>{deleteProjectData.project_name}</strong>?
              </p>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  onClick={() => setShowDelete(false)}
                  className="px-4 py-2 bg-gray-300 rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={deleteProject}
                  className="px-4 py-2 bg-red-600 text-white rounded"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
